import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { MapPin, Truck, TreePine } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BlurredImageContainer } from "@/components/BlurredImageContainer";
import { useIsMobile } from "@/hooks/use-mobile";
import { logListingEvent } from "@/lib/listingEvents";
import type { Listing } from "@/lib/mockData";

const categoryIcons: Record<string, string> = {
  ved: "🪵",
  julgran: "🎄",
  plank: "📏",
  stockar: "🪓",
  "enskilda träd": "🌳",
};

const deliveryLabel: Record<string, string> = {
  hämta: "Hämta",
  leverans: "Leverans",
  båda: "Hämta / Leverans",
};

export function ListingCard({ listing, priority = false }: { listing: Listing; priority?: boolean }) {
  const isMobile = useIsMobile();
  const cardRef = useRef<HTMLAnchorElement>(null);
  const viewLoggedRef = useRef(false);

  // Log a `view` event the first time the card is at least partially visible.
  useEffect(() => {
    const el = cardRef.current;
    if (!el || viewLoggedRef.current) return;
    if (typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !viewLoggedRef.current) {
            viewLoggedRef.current = true;
            logListingEvent(listing.id, "view");
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [listing.id]);

  const handleClick = () => {
    logListingEvent(listing.id, "detail_click");
  };

  if (isMobile) {
    return (
      <Link
        ref={cardRef}
        to={`/annonser/${listing.id}`}
        onClick={handleClick}
        className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm"
      >
        {/* Square image */}
        {listing.images && listing.images.length > 0 ? (
          <div className="relative overflow-hidden bg-secondary" style={{ aspectRatio: "1/1" }}>
            <img
              src={listing.images[0]}
              alt=""
              aria-hidden
              loading={priority ? "eager" : "lazy"}
              decoding="async"
              // @ts-expect-error fetchpriority is a valid HTML attribute
              fetchpriority={priority ? "high" : "auto"}
              className="absolute inset-0 h-full w-full object-cover blur-[14px] scale-110 brightness-[0.6]"
            />
            <img
              src={listing.images[0]}
              alt={listing.title}
              loading={priority ? "eager" : "lazy"}
              decoding="async"
              // @ts-expect-error fetchpriority is a valid HTML attribute
              fetchpriority={priority ? "high" : "auto"}
              className="relative z-10 h-full w-full object-cover"
            />
          </div>
        ) : (
          <div className="flex items-center justify-center bg-secondary" style={{ aspectRatio: "1/1" }}>
            <span className="text-3xl">{categoryIcons[listing.category] ?? "🌲"}</span>
          </div>
        )}

        <div className="flex flex-col gap-0.5 p-2.5">
          {/* Price */}
          {listing.price > 0 ? (
            <span className="text-sm font-bold text-heading">
              {listing.price.toLocaleString("sv-SE")} kr
              {listing.priceUnit ? <span className="ml-1 text-[11px] font-normal text-muted-foreground">{listing.priceUnit}</span> : null}
            </span>
          ) : (
            <span className="text-sm font-bold text-accent">Prisförslag</span>
          )}

          {/* Title */}
          <h3 className="text-xs font-medium leading-tight text-heading line-clamp-2">
            {listing.title}
          </h3>

          {/* Location */}
          <span className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{listing.location}</span>
          </span>

          {/* Delivery */}
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Truck className="h-3 w-3 shrink-0" />
            <span className="truncate">{deliveryLabel[listing.delivery]}</span>
          </span>
        </div>
      </Link>
    );
  }

  // Desktop/tablet layout (unchanged)
  return (
    <Link
      ref={cardRef}
      to={`/annonser/${listing.id}`}
      onClick={handleClick}
      className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-card transition-all hover:shadow-card-hover"
    >
      {listing.images && listing.images.length > 0 ? (
        <BlurredImageContainer
          src={listing.images[0]}
          alt={listing.title}
          className="rounded-t-lg"
          priority={priority}
        />
      ) : (
        <div className="flex items-center justify-center bg-secondary" style={{ aspectRatio: "4/3" }}>
          <span className="text-4xl">{categoryIcons[listing.category] ?? "🌲"}</span>
        </div>
      )}
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-2 flex items-center gap-2">
          <Badge variant="outline" className="text-xs capitalize">
            {listing.category}
          </Badge>
          <Badge variant="secondary" className="text-xs capitalize">
            {listing.species}
          </Badge>
        </div>

        <h3 className="mb-1 font-sans text-base font-semibold leading-tight group-hover:text-primary transition-colors">
          {listing.title}
        </h3>

        <div className="mb-3 mt-1 flex flex-1 items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {listing.location}</span>
          <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> {deliveryLabel[listing.delivery]}</span>
        </div>

        <div className="flex items-center justify-between">
          {listing.price > 0 ? (
            <span className="font-semibold text-heading">
              {listing.price.toLocaleString("sv-SE")} kr <span className="text-xs font-normal text-muted-foreground">{listing.priceUnit}</span>
            </span>
          ) : (
            <span className="text-sm font-medium text-accent">Prisförslag</span>
          )}
          <Button size="sm" variant="outline">Visa</Button>
        </div>
      </div>
    </Link>
  );
}
